import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Tactmark Post-Match',
  alternateName: ['Tactmark soccer analysis software', 'Tactmark football analysis software'],
  applicationCategory: 'SportsApplication',
  operatingSystem: 'Web',
  description:
    'Soccer analysis software for coaches and clubs. Tactmark Post-Match turns match evidence into coaching decisions, training priorities, and later-match verification.',
  url: 'https://tactmark.com/soccer-analysis-software',
  provider: { '@type': 'Organization', name: 'Tactmark', url: 'https://tactmark.com' },
};

const FAQS = [
  {
    q: 'What is soccer analysis software?',
    a: 'Soccer analysis software helps coaches review completed matches, organize evidence, identify tactical problems, and turn findings into training priorities.',
  },
  {
    q: 'Is Tactmark soccer analysis software or football analysis software?',
    a: 'Both. Tactmark covers football and soccer terminology for the same Post-Match coaching decision workflow.',
  },
  {
    q: 'Do I need GPS or video hardware?',
    a: 'No. Structured coach observation is enough to generate a complete coaching report. Video and GPS are optional.',
  },
];

export default function SoccerAnalysisSoftware() {
  usePageMeta(
    'Soccer Analysis Software for Coaches and Clubs | Tactmark',
    'Soccer analysis software for coaches and clubs. Tactmark Post-Match turns match evidence into coaching decisions, training priorities, and later-match verification — no GPS required.'
  );

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
      <PublicNav />
      <main className="bg-white">
        <section className="bg-navy-900 text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-4">
              Soccer Analysis Software · Football Analysis · Post-Match Review
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              Soccer Analysis Software That Coaches Finish After the Match
            </h1>
            <p className="text-navy-200 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              Tactmark Post-Match turns match evidence into coaching decisions, training priorities, and
              later-match verification — for clubs searching for soccer analysis or football analysis software.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login?mode=signup"
                className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors"
              >
                Start free trial
              </Link>
              <Link
                to="/football-analysis-software"
                className="border border-navy-400 text-white font-semibold px-8 py-3.5 rounded-xl hover:border-white transition-colors"
              >
                Football analysis software
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-4">
              What soccer analysis software should deliver
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Clip libraries help you watch. Soccer analysis for coaching staff needs a decision record staff
              can confirm and revisit.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                'Match verdict tied to submitted evidence',
                'Confirmed findings with staff review states',
                'Training priorities linked to decisive risks',
                'Season memory for recurring problems',
              ].map((item) => (
                <div key={item} className="border border-gray-200 rounded-2xl p-6 flex gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  <p className="text-navy-900 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Related analysis pages</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { to: '/football-analysis-software', label: 'Football analysis software' },
                { to: '/soccer-coaching-tools', label: 'Soccer coaching tools' },
                { to: '/post-match-analysis-engine', label: 'Post-match analysis engine' },
                { to: '/insights', label: 'Football & soccer analysis insights' },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-navy-400 transition-colors group"
                >
                  <span className="text-navy-900 text-sm font-medium">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-navy-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Frequently asked questions</h2>
            <div className="space-y-6">
              {FAQS.map((f) => (
                <div key={f.q}>
                  <h3 className="text-navy-900 font-semibold mb-2">{f.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
